import React, { useEffect, useState, useRef } from "react"
import Typography from "aldoo-ra/Typography"
import getRealtimeDeploymentController from "./realtime-deployment-controller"

const cellTone = (v) => {
  if (v == null) return "bg-admin_neutral_1"
  if (v >= 0.999) return "bg-admin_success"
  if (v >= 0.8) return "bg-admin_warning"
  return "bg-admin_error"
}

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS

// Pad the sparse history array to the full [from, to] range at the given
// granularity. Missing buckets become { value: null }. Output is ordered
// oldest → newest so the rendered row has "now" on the right.
const padHistory = ({ history, from, to, granularity }) => {
  const out = []
  if (!from || !to) return Array.isArray(history) ? history.slice() : []
  const start = new Date(from).getTime()
  const end = new Date(to).getTime()
  if (!isFinite(start) || !isFinite(end) || end <= start) {
    return Array.isArray(history) ? history.slice() : []
  }
  const step = granularity === "day" ? DAY_MS : HOUR_MS
  const byBucket = new Map()
  for (const b of history || []) {
    const t = new Date(b?.timestamp).getTime()
    if (!isFinite(t)) continue
    // Snap to bucket start (floor to step).
    const slot = Math.floor(t / step) * step
    byBucket.set(slot, b)
  }
  for (let t = Math.floor(start / step) * step; t < end; t += step) {
    const existing = byBucket.get(t)
    out.push(existing || { timestamp: new Date(t).toISOString(), value: null })
  }
  return out
}

const formatTimestamp = (ts) => {
  if (!ts) return ""
  const d = new Date(ts)
  if (isNaN(d.getTime())) return ts
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const Tile = ({ label, value, tone = "" }) => (
  <div className="p-4 rounded-lg border border-admin_bordered bg-admin_canvas">
    <div className="text-[11px] uppercase tracking-wider text-admin_text_secondary font-medium">
      {label}
    </div>
    <div className={`text-lg font-bold mt-1 ${tone}`}>{value}</div>
  </div>
)

const AppStatusTab = ({ item }) => {
  const [status, setStatus] = useState(item.status || null)
  const controllerRef = useRef(null)

  const getSocket = () => {
    if (!controllerRef.current) controllerRef.current = getRealtimeDeploymentController()
    return controllerRef.current?.socket
  }

  // Subscribe to live broadcasts for this app id (compare against item.id which
  // is composite for env apps; the server emits with the plain id, so we match
  // by suffix as well).
  useEffect(() => {
    const socket = getSocket()
    if (!socket) return
    const targetId = item.id
    const plainId = typeof targetId === "string" && targetId.includes(":")
      ? targetId.split(":").pop()
      : targetId
    const handler = (data) => {
      if (!data) return
      if (data.app_id === targetId || data.app_id === plainId) {
        setStatus(data.status || null)
      }
    }
    socket.on("deployment:app_status_updated", handler)
    return () => socket.off("deployment:app_status_updated", handler)
  }, [item.id])

  if (!status) {
    return (
      <div className="px-3 py-4 text-xs text-admin_text_secondary italic">
        No status hook configured. Add <code className="font-mono">status.hook</code> and{" "}
        <code className="font-mono">status.api_key</code> in the Settings tab to enable.
      </div>
    )
  }

  if (status.ok === false) {
    return (
      <div className="space-y-3">
        <div className="px-3 py-2 text-xs text-admin_error bg-admin_error/10 border border-admin_error/30 rounded">
          Hook error: {status.error || "unreachable"}
        </div>
        <div className="text-[11px] text-admin_text_secondary">
          Last attempt: {formatTimestamp(status.checkedAt)} ({status.elapsedMs}ms)
        </div>
      </div>
    )
  }

  // "Current" state = most-recent non-null bucket value. Window average is
  // shown separately in the Uptime tile.
  const currentValue = (() => {
    const h = Array.isArray(status.history) ? status.history : []
    for (let i = h.length - 1; i >= 0; i--) {
      const v = h[i]?.value
      if (typeof v === "number") return v
    }
    return typeof status.uptime === "number" ? status.uptime : null
  })()
  const stateLabel =
    currentValue == null
      ? "Unknown"
      : currentValue >= 0.999
      ? "Operational"
      : currentValue >= 0.8
      ? "Degraded"
      : "Down"
  const stateTone =
    currentValue == null
      ? ""
      : currentValue >= 0.999
      ? "text-admin_success"
      : currentValue >= 0.8
      ? "text-admin_warning"
      : "text-admin_error"

  const history = padHistory({
    history: status.history,
    from: status.from,
    to: status.to,
    granularity: status.granularity || "hour",
  })

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Tile label="Status" value={stateLabel} tone={stateTone} />
        <Tile
          label={`Uptime${status.days ? ` (${status.days}d)` : ""}`}
          value={status.uptimePct != null ? `${status.uptimePct}%` : "—"}
        />
        <Tile
          label="Avg Latency"
          value={status.avgLatencyMs != null ? `${status.avgLatencyMs}ms` : "—"}
        />
      </div>

      {history.length > 0 && (
        <div className="p-4 rounded-lg border border-admin_bordered bg-admin_canvas space-y-2">
          <div className="flex items-center justify-between">
            <Typography variant="admin_h4">
              {status.days ? `${status.days}-day` : "Recent"} uptime
              {status.granularity ? ` · ${status.granularity}` : ""}
            </Typography>
            <span className="text-[11px] text-admin_text_secondary font-mono">
              {history.length} buckets
            </span>
          </div>
          <div className="flex items-stretch gap-px h-7">
            {history.map((b, i) => {
              const v = typeof b?.value === "number" ? b.value : null
              const title = `${formatTimestamp(b?.timestamp)} — ${
                v == null ? "no data" : `${(v * 100).toFixed(2)}%`
              }`
              return (
                <span
                  key={`${b?.timestamp || i}`}
                  title={title}
                  className={`flex-1 rounded-sm ${cellTone(v)}`}
                  style={{ minWidth: 2 }}
                />
              )
            })}
          </div>
          <div className="flex justify-between text-[11px] text-admin_text_secondary">
            <span>{status.days ? `${status.days}d ago` : "past"}</span>
            <span>now</span>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-admin_text_secondary pt-1">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-admin_success" /> ≥ 99.9%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-admin_warning" /> ≥ 80%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-admin_error" /> &lt; 80%
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-sm bg-admin_neutral_1" /> no data
            </span>
          </div>
        </div>
      )}

      <div className="text-[11px] text-admin_text_secondary">
        Checked {formatTimestamp(status.checkedAt)} · hook responded in {status.elapsedMs}ms
        {status.componentName ? ` · component "${status.componentName}"` : ""}
      </div>
    </div>
  )
}

export default AppStatusTab
